/**
 * Utility functions for calibration operations
 */

import { CUSTOM_RATING_ORDER } from '../constants';

/**
 * Checks if a number has exactly one digit after the decimal point
 * @param {number} num - The number to check
 * @returns {boolean}
 */
export const hasOneDigitAfterDecimal = (num) => {
   const numStr = num.toString();
   const decimalIndex = numStr.indexOf('.');
   return decimalIndex !== -1 && numStr.length - decimalIndex === 2;
};

/**
 * Gets the previous score and rating from calibration history
 * @param {Object} user - User object with calibration scores
 * @param {Object} calibratedScore - Current calibrated scores
 * @returns {Object} Previous score and rating
 */
export const getPreviousScoreAndRating = (user) => {
   let scoreBefore = 0;
   let ratingBefore = '';
   let calLength = user.CalibrationScores.length - 2;

   while (calLength >= 0) {
      scoreBefore = user.CalibrationScores[calLength].CalibrationScore;
      ratingBefore = user.CalibrationScores[calLength].CalibrationRating;
      calLength--;

      if (scoreBefore && ratingBefore) {
         break;
      }
   }

   // Fallback to actual score if no previous calibration exists
   if (!scoreBefore && !ratingBefore) {
      scoreBefore = user.ActualScores[0]?.ActualScore;
      ratingBefore = user.ActualScores[0]?.ActualRating;
   }

   return { scoreBefore, ratingBefore };
};

/**
 * Determines if a score has changed from previous calibration
 * @param {Object} user - User object
 * @param {Object} calibratedScore - Current calibrated scores
 * @returns {string} CSS class name for highlighting
 */
export const getScoreChangeClass = (user, calibratedScore) => {
   const { scoreBefore, ratingBefore } = getPreviousScoreAndRating(user);
   const currentScore = calibratedScore[user.ID]?.CalibrationScore;
   const currentRating = calibratedScore[user.ID]?.CalibrationRating;

   const hasScoreChanged = user.ScoringMethod === 'Score' && scoreBefore !== currentScore;
   const hasRatingChanged = user.ScoringMethod === 'Rating' && ratingBefore !== currentRating;

   return hasScoreChanged || hasRatingChanged
      ? 'text-center highlight-change-score'
      : 'text-center highlight';
};

/**
 * Cycles through sort states
 * @param {string} currentState - Current sort state
 * @returns {string} Next sort state
 */
export const getNextSortState = (currentState) => {
   const states = ['default', 'ascending', 'descending'];
   const currentIndex = states.indexOf(currentState);
   return states[(currentIndex + 1) % states.length];
};

/**
 * Calculates deviation between calibrated and quota ratings
 * @param {Object} countCalibrated - Calibrated counts
 * @param {Object} ratingQuota - Rating quotas
 * @returns {Object} Deviation for each rating
 */
export const calculateRatingDeviation = (countCalibrated, ratingQuota) => {
   return {
      APlus: countCalibrated.APlus - ratingQuota.APlus,
      A: countCalibrated.A - ratingQuota.A,
      BPlus: countCalibrated.BPlus - ratingQuota.BPlus,
      B: countCalibrated.B - ratingQuota.B,
      C: countCalibrated.C - ratingQuota.C,
      D: countCalibrated.D - ratingQuota.D
   };
};

/**
 * Formats a date to locale string
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDeadlineDate = (date) => {
   if (!date) return '';
   return new Date(date).toLocaleString('default', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
   });
};

/**
 * Downloads an Excel file from the server
 * @param {Function} axiosInstance - Axios instance
 * @param {string} path - Path to download from
 * @param {Function} toast - Toast notification function
 */
export const downloadExcelFile = async (axiosInstance, path, toast) => {
   try {
      const response = await axiosInstance.get(path, {
         responseType: 'arraybuffer'
      });

      const blob = new Blob([response.data], {
         type: response.headers['content-type']
      });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'report.xlsx';
      link.click();

      window.URL.revokeObjectURL(link.href);
   } catch (error) {
      const errorMessage = error.response?.data?.status?.description
         || error.message
         || 'Failed to download file';
      toast.error(errorMessage);
   }
};
